<script setup lang="ts">
/**
 * SectionSplitContent
 *
 * Two-column layout with text content and media (image/video).
 * Supports multiple content variants and image styling treatments.
 *
 * Variants:
 * - 'paragraphs' (default): Standard paragraph content
 * - 'benefits-list': Vertical list with icons (great for breaking grid fatigue)
 *
 * Image Styles:
 * - 'default': Simple rounded corners
 * - 'styled': Rounded + shadow (Metwide treatment)
 * - 'accent-box': Styled + offset mw-accent box behind
 */

interface BenefitItem {
  icon?: string;
  title: string;
  description?: string;
}

interface MediaProps {
  type: "image" | "video";
  src: string;
  alt?: string;
}

interface SectionSplitContentProps {
  title: string;
  content?: string[];
  benefits?: BenefitItem[];
  media?: MediaProps;
  layout?: "content-left" | "content-right";
  sectionBg?: "white" | "neutral";
  variant?: "paragraphs" | "benefits-list";
  imageStyle?: "default" | "styled" | "accent-box";
}

const props = withDefaults(defineProps<SectionSplitContentProps>(), {
  content: () => [],
  benefits: () => [],
  media: undefined,
  layout: "content-left",
  sectionBg: "white",
  variant: "paragraphs",
  imageStyle: "styled",
});

const bgClass = computed(() =>
  props.sectionBg === "neutral" ? "bg-neutral-50" : "bg-white"
);

const gridOrderClass = computed(() =>
  props.layout === "content-right" ? "lg:order-2" : ""
);

const mediaOrderClass = computed(() =>
  props.layout === "content-right" ? "lg:order-1" : ""
);

const imageContainerClass = computed(() => {
  const base = "relative overflow-hidden rounded-2xl";

  switch (props.imageStyle) {
    case "accent-box":
    case "styled":
      return `${base} shadow-xl shadow-slate-200/50`;
    case "default":
    default:
      return base;
  }
});

/**
 * Convert icon name to Nuxt Icon format
 * Handles: "Shield" -> "lucide:shield", "ShieldCheck" -> "lucide:shield-check"
 * Also handles already-formatted names like "lucide:shield"
 */
const formatIconName = (iconName?: string): string => {
  if (!iconName) return "lucide:check";
  if (iconName.includes(":")) return iconName;

  // Convert PascalCase to kebab-case
  const kebab = iconName.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();

  return `lucide:${kebab}`;
};

</script>

<template>
  <section :class="[bgClass, 'py-16 sm:py-24']">
    <div class="mx-auto max-w-7xl px-8">
      <div :class="media ? 'grid items-center gap-12 lg:grid-cols-2 lg:gap-16' : 'max-w-3xl'">
        <!-- Content Column -->
        <div :class="gridOrderClass">
          <h2
            class="mb-6 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl"
          >
            {{ title }}
          </h2>

          <!-- Paragraphs Variant -->
           <!-- eslint-disable vue/no-v-html -->
          <div v-if="variant === 'paragraphs' && content" class="space-y-4">
            <p
              v-for="(paragraph, index) in content"
              :key="index" class="text-lg leading-7 text-neutral-600"
              v-html="paragraph"
            >
            </p>
          </div>

          <!-- Benefits List Variant -->
          <div v-else-if="variant === 'benefits-list' && benefits" class="space-y-6">
            <div v-for="(benefit, index) in benefits" :key="index" class="flex gap-4">
              <!-- Icon -->
              <div
                class="flex size-12 shrink-0 items-center justify-center rounded-xl bg-mw-accent-50"
              >
                <Icon
                  :name="formatIconName(benefit.icon)"
                  size="24"
                  class="text-mw-accent-500"
                />
              </div>

              <!-- Text -->
              <div>
                <h3 class="font-semibold text-slate-900">
                  {{ benefit.title }}
                </h3>
                <!-- eslint-disable vue/no-v-html -->
                <p v-if="benefit.description" class="mt-1 text-neutral-600" v-html="benefit.description">
                 
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Media Column (only render if media is provided) -->
        <div v-if="media" :class="mediaOrderClass">
          <!-- Accent Box Wrapper (for accent-box style) -->
          <div v-if="imageStyle === 'accent-box'" class="relative">
            <!-- Offset mw-accent Accent -->
            <div
              class="absolute -bottom-3 -right-3 size-full rounded-2xl bg-mw-accent-400/20"
              aria-hidden="true"
            ></div>

            <!-- Image Container -->
            <div :class="imageContainerClass" class="relative">
              <ResponsiveImage
                v-if="media.type === 'image'"
                :src="media.src"
                :alt="media.alt || title"
                class="size-full object-cover"
                loading="eager"
                :width="1200"
                :height="675"
              />
              <video
                v-else-if="media.type === 'video'"
                :src="media.src"
                class="size-full object-cover"
                controls
                preload="metadata"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>

          <!-- Standard Image Container (for default/styled) -->
          <div v-else :class="imageContainerClass">
            <ResponsiveImage
              v-if="media.type === 'image'"
              :src="media.src"
              :alt="media.alt || title"
              class="size-full object-cover"
              loading="eager"
              :width="1200"
              :height="675"
            />
            <video
              v-else-if="media.type === 'video'"
              :src="media.src"
              class="size-full object-cover"
              controls
              preload="metadata"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
